import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, Star } from "lucide-react";
import CTAButton from "../CTAButton";

const CombinedBenefitsSection: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [timeLeft, setTimeLeft] = useState({
    minutes: 15,
    seconds: 0
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { minutes: prev.minutes - 1, seconds: 59 };
        } else {
          return { minutes: 0, seconds: 0 };
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);


  const benefits = [
    "You Discover How To Grow Your Salon With Smart System & Software",
    "You Learn Proven Strategies To Manage Customers & Increase Retention",
    "You Gain Insights To Boost Revenue With Better Data & Analytics",
    "You Unlock Freedom To Scale Your Salon Smoothly & Stress-Free",
    "Unlock AI-driven tools to keep clients coming back & your calendar full."
  ];

  const webinarData = [
    {
      id: 1,
      image: "/Kumar-subramani.webp",
      coachName: "Kumar Subramani",
      coachTitle: "Co-Founder and CEO at Zylu",
      coachDescription: "750+ Salon Owner Interviews, 500+ Salon Customer Interviews, 5+ years in Marketing.",
      ctaText: "REGISTER NOW AT ₹299/- ONLY",
      calendlyUrl: "https://calendly.com/zylusales/group-growth-consultation?hide_gdpr_banner=1"
    },
    {
      id: 2,
      image: "/Pavithra-soundarrajan.webp",
      coachName: "Pavithra Soundararajan",
      coachTitle: "Co-Founder and Chief Growth at Zylu",
      coachDescription: "3000+ Salon Owner Interviews, 750+ Salon Customer Interviews, 5+ years in Marketing",
      ctaText: "REGISTER NOW AT ₹299/- ONLY",
      calendlyUrl: "https://calendly.com/zylusales/group-growth-consultation?hide_gdpr_banner=1"
    },
   
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % webinarData.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + webinarData.length) % webinarData.length);
  };

  const currentWebinar = webinarData[currentSlide];

  return (
    <section className="pt-2 md:pt-4 pb-12 md:pb-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-8 md:mb-16"
        >
          {/* <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            What Happens When You Join?
          </h2> */}
        </motion.div>

        {/* Slider Container */}
        <div className="relative">

          {/* Coach Information - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-12 md:mb-16">
            {webinarData.map((webinar, index) => (
              <motion.div
                key={webinar.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6"
              >
                {/* Coach Image */}
                <div className="flex-shrink-0 w-full sm:w-auto flex justify-center sm:justify-start">
                  <img 
                    src={webinar.image} 
                    alt={webinar.coachName} 
                    className="w-48 h-64 sm:w-56 sm:h-72 rounded-lg shadow-2xl object-cover object-top"
                  />
                </div>

                {/* Coach Details */}
                <div className="flex-1 space-y-4 text-center sm:text-left">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-black">
                    {webinar.coachName}
                  </h3>

                  <p className="text-base sm:text-lg text-black">
                    {webinar.coachTitle}
                  </p>

                  <div className="w-24 h-0.5 bg-red-500 mx-auto sm:mx-0"></div>

                  <p className="text-black text-sm sm:text-base">
                    {webinar.coachDescription}
                  </p>

                  {/* Rating */}
                  <div className="flex items-center justify-center sm:justify-start space-x-2">
                    <div className="flex text-yellow-500">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} fill="currentColor" className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5" />
                      ))}
                    </div>
                    <span className="text-black text-xs sm:text-sm">
                      Globally Serving Salon Growth
                    </span>
                  </div>

                  {/* CTA Button */}
                  <div className="pt-4">
                    <CTAButton
                      text={webinar.ctaText}
                      calendlyUrl={webinar.calendlyUrl}
                      className="text-sm sm:text-base py-3 px-6 w-full uppercase tracking-wide font-bold"
                      icon={false}
                      isWebinarRegistration={true}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Benefits List - Block Level Below */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
              {/* Benefits List - Single Card */}
                     <motion.div
                       initial={{ opacity: 0, y: 20 }}
                       whileInView={{ opacity: 1, y: 0 }}
                       transition={{ duration: 0.6, delay: 0.4 }}
                       viewport={{ once: true }}
                       className="bg-white rounded-2xl p-4 md:p-8 shadow-xl border-2 border-red-500"
                     >
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-black mb-4 md:mb-6 text-center">
                What will you learn?

                </h3>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <div
                      key={index}
                      className="flex items-start"
                    >
                      <div className="bg-red-500 rounded-full p-1 md:p-2 mr-3 md:mr-4 flex-shrink-0 mt-1">
                        <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-white" />
                      </div>
                      <p className="text-black text-sm sm:text-base md:text-lg leading-relaxed">
                        {benefit}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default CombinedBenefitsSection;
